import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, Wallet } from 'lucide-react'
import { Badge, Card, ProgressBar } from '@/components/ui'
import { getPersonalManagerRoyaltyStatus } from '@/services/personalManagerService'
import type { PersonalManagerRoyaltyPaymentStatus, PersonalManagerRoyaltyStatusRow } from '@/types'
import { formatCurrency, formatPartnerDisplay, formatPercentage } from '@/lib/utils'

const MONTHS = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь']

const STATUS: Record<PersonalManagerRoyaltyPaymentStatus, { text: string; variant: 'success' | 'warning' | 'error' | 'neutral' | 'info' }> = {
  unpaid: { text: 'Не оплачено', variant: 'error' },
  partial: { text: 'Частично', variant: 'warning' },
  paid: { text: 'Оплачено', variant: 'success' },
  overpaid: { text: 'Переплата', variant: 'info' },
  no_charge: { text: 'Нет начисления', variant: 'neutral' },
}

function isDebtor(row: PersonalManagerRoyaltyStatusRow) {
  return row.payment_status === 'unpaid' || row.payment_status === 'partial'
}

function formatShortDate(value: string | null) {
  if (!value) return ''
  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Moscow' }).format(new Date(value))
}

/**
 * Требование на оплату — чтобы ЛМ не звонил франчайзи, которому ещё не выставили счёт:
 * это вопрос к бухгалтерии, а не к франчайзи.
 */
function requestLabel(row: PersonalManagerRoyaltyStatusRow): { text: string; warn: boolean } | null {
  if (!isDebtor(row)) return null
  if (row.requests_sent_count > 0) return { text: `требование отправлено ${formatShortDate(row.last_request_sent_at)}`, warn: false }
  if (row.requests_count > 0) return { text: 'требование подготовлено, не отправлено', warn: true }
  return { text: 'требования нет', warn: true }
}

export function PersonalManagerRoyaltyCard({ managerUserId }: { managerUserId: string }) {
  const [rows, setRows] = useState<PersonalManagerRoyaltyStatusRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    getPersonalManagerRoyaltyStatus(managerUserId)
      .then(data => { if (!cancelled) setRows(data) })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : String(err)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [managerUserId])

  const summary = useMemo(() => {
    const charged = rows.reduce((sum, row) => sum + row.charged_rub, 0)
    const paid = rows.reduce((sum, row) => sum + row.paid_rub, 0)
    const debt = rows.reduce((sum, row) => sum + row.debt_rub, 0)
    const counts: Record<PersonalManagerRoyaltyPaymentStatus, number> = { unpaid: 0, partial: 0, paid: 0, overpaid: 0, no_charge: 0 }
    for (const row of rows) counts[row.payment_status] += 1
    return { charged, paid, debt, counts, pct: charged > 0 ? (paid / charged) * 100 : null }
  }, [rows])

  const first = rows[0]
  const monthLabel = first ? `${MONTHS[first.royalty_month - 1]} ${first.royalty_year}` : 'прошлый месяц'
  const debtors = rows.filter(isDebtor)
  const visible = showAll ? rows : debtors

  return (
    <Card className="p-4">
      <button
        type="button"
        className="flex w-full items-start justify-between gap-3 text-left"
        onClick={() => setExpanded(value => !value)}
        aria-expanded={expanded}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Wallet size={16} className="text-primary" />
            Роялти за {monthLabel}
            <ChevronDown size={16} className={`text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            Оплачено — деньги, зачёты и списания. Суммы в рублях.
          </div>
        </div>
        {!loading && !error && rows.length > 0 && (
          <div className="text-right">
            <div className="text-lg font-semibold">{summary.pct == null ? '—' : formatPercentage(summary.pct)}</div>
            <div className="text-xs text-muted-foreground">
              {formatCurrency(summary.paid)} / {formatCurrency(summary.charged)}
              {!expanded && debtors.length > 0 ? ` · должников ${debtors.length}` : ''}
            </div>
          </div>
        )}
      </button>

      {loading && <div className="mt-3 text-sm text-muted-foreground">Загрузка...</div>}
      {error && <div className="mt-3 text-sm text-destructive">Роялти не загрузились: {error}</div>}

      {expanded && !loading && !error && rows.length === 0 && (
        <div className="mt-3 text-sm text-muted-foreground">Франчайзи не назначены.</div>
      )}

      {expanded && !loading && !error && rows.length > 0 && (
        <>
          <ProgressBar value={summary.pct ?? 0} className="mt-3" />
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <Badge text={`Не оплачено: ${summary.counts.unpaid}`} variant="error" />
            <Badge text={`Частично: ${summary.counts.partial}`} variant="warning" />
            <Badge text={`Оплачено: ${summary.counts.paid + summary.counts.overpaid}`} variant="success" />
            {summary.counts.no_charge > 0 && <Badge text={`Нет начисления: ${summary.counts.no_charge}`} variant="neutral" />}
            {summary.debt > 0.005 && <span className="self-center text-muted-foreground">Долг всего {formatCurrency(summary.debt)}</span>}
          </div>

          {visible.length === 0 ? (
            <div className="mt-3 text-sm text-muted-foreground">Должников нет — все закреплённые франчайзи оплатили.</div>
          ) : (
            <div className="mt-3 divide-y divide-border">
              {visible.map(row => {
                const status = STATUS[row.payment_status]
                const request = requestLabel(row)
                return (
                  <div key={row.partner_number} className="flex items-start justify-between gap-3 py-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">
                        {formatPartnerDisplay(row.partner_number, row.persons_fio, row.cities_text)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        начислено {formatCurrency(row.charged_rub)} · оплачено {formatCurrency(row.paid_rub)}
                        {row.written_off_rub > 0.005 ? ` (из них списано ${formatCurrency(row.written_off_rub)})` : ''}
                      </div>
                      {request && (
                        <div className={`text-xs ${request.warn ? 'text-warning' : 'text-muted-foreground'}`}>{request.text}</div>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      <Badge text={status.text} variant={status.variant} className="text-xs" />
                      {row.debt_rub > 0.005 && (
                        <div className="mt-1 text-sm font-semibold text-destructive">{formatCurrency(row.debt_rub)}</div>
                      )}
                      {row.payment_status === 'overpaid' && (
                        <div className="mt-1 text-xs text-muted-foreground">+{formatCurrency(row.credit_rub)}</div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {debtors.length !== rows.length && (
            <button
              className="mt-2 text-xs font-medium text-primary hover:underline"
              onClick={() => setShowAll(value => !value)}
            >
              {showAll ? 'Показать только должников' : `Показать всех (${rows.length})`}
            </button>
          )}
        </>
      )}
    </Card>
  )
}
